import React, { useState, useEffect, useCallback, useRef } from "react";
import { YouTubePlayer } from "youtube-player/dist/types";
import { css } from "@emotion/css";
import { useIdeasNodeStore } from "../../../stores/ideaNodesStore";
import { sendExtensionMessage } from "@sharpana/auth/api_client/allauth";

interface SegmentProgressBarProps {
  youtubePlayer: YouTubePlayer | null;
  startTimeInSeconds: number;
  endTimeInSeconds: number;
  onTimeUpdate?: (time: number) => void;
  isHovered: boolean;
}

const containerStyles = css({
  width: "100%",
  margin: "0 auto",
  position: "relative",
  userSelect: "none",
});

const currentTimeStyles = css({
  backgroundColor: "rgba(0, 0, 0, 0.8)",
  color: "white",
  padding: "4px 8px",
  fontSize: "0.875rem",
  fontWeight: 500,
  textAlign: "center",
  position: "absolute",
  bottom: "33px",
  left: "4px",
  minWidth: "50px",
  width: "fit-content",
  marginBottom: "8px",
  borderRadius: "4px",
});

const getProgressBarContainerStyles = (isHovered: boolean) =>
  css({
    position: "relative",
    height: "4px",
    cursor: "pointer",
    "&::before": {
      content: '""',
      position: "absolute",
      top: "50%",
      transform: "translateY(-50%)",
      left: 0,
      right: 0,
      height: `${isHovered ? "4px" : "1px"}`,
      backgroundColor: "#e2e8f0",
      transition: "height 0.2s ease",
    },
    "&:hover .progressHandle": {
      transform: "translate(-50%, -50%) scale(1.2)",
    },
  });

const getProgressStyles = (progress: number) =>
  css({
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    height: "inherit",
    width: `${progress}%`,
    backgroundColor: "#ff0034",
    transition: "width 0.1s ease-out, height 0.2s ease",
  });

const getHandleStyles = (progress: number, isHovered: boolean) =>
  css({
    display: `${isHovered ? "inherit" : "none"}`,
    position: "absolute",
    height: "14px",
    width: "14px",
    backgroundColor: "#ff0034",
    borderRadius: "50%",
    top: "50%",
    left: `${progress}%`,
    transform: "translate(-50%, -50%)",
    cursor: "grab",
    transition: "transform 0.2s ease",
    zIndex: 100,
    "&:active": {
      cursor: "grabbing",
      transform: "translate(-50%, -50%) scale(1.2)",
    },
  });

const getTimeDisplayStyles = (isHovered: boolean) =>
  css({
    display: "flex",
    justifyContent: "space-between",
    cursor: "pointer",
    marginTop: "0.5rem",
    fontSize: "0.9rem",
    color: "rgba(255, 255, 255, 0.8)", // Changed to be more visible on dark gradient
    fontWeight: `${isHovered ? "600" : "400"}`,
    lineHeight: "0.8", // Prevent vertical clipping of descenders
    overflow: "visible",
  });

// Helper function to format seconds into HH:MM:SS or M:SS
const formatTime = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = Math.floor(seconds % 60);

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, "0")}:${remainingSeconds
      .toString()
      .padStart(2, "0")}`;
  }
  return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
};

const SegmentProgressBar: React.FC<SegmentProgressBarProps> = ({
  youtubePlayer,
  startTimeInSeconds,
  endTimeInSeconds,
  onTimeUpdate,
  isHovered,
}) => {
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState(startTimeInSeconds);
  const [isDragging, setIsDragging] = useState(false);
  const segmentDuration = endTimeInSeconds - startTimeInSeconds;

  // Simple function to stop event propagation
  const stopPropagation = (e: React.UIEvent) => {
    e.stopPropagation();
  };
  


  useEffect(() => {
    if (!youtubePlayer) return;

    let animationFrameId: number;
    let lastUpdateTime = 0;

    // Use requestAnimationFrame for smoother progress updates
    const updateProgress = async () => {
      const now = Date.now();
      
      // Only update every 100ms for efficiency
      if (!isDragging && now - lastUpdateTime > 100) {
        lastUpdateTime = now;
        try {
          const time = await youtubePlayer.getCurrentTime();
          setCurrentTime(time);
          const segmentProgress = Math.max(
            0,
            Math.min(100, ((time - startTimeInSeconds) / segmentDuration) * 100)
          );
          setProgress(segmentProgress);

          if (time >= endTimeInSeconds) {
            youtubePlayer.pauseVideo();
          }
        } catch (error) {
          console.error("Error getting current time:", error);
        }
      }
      
      // Continue the animation loop
      animationFrameId = requestAnimationFrame(updateProgress);
    };

    // Start the animation loop
    animationFrameId = requestAnimationFrame(updateProgress);

    // Cleanup function
    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [
    youtubePlayer,
    startTimeInSeconds,
    endTimeInSeconds,
    segmentDuration,
    isDragging,
  ]);


  
  // Handle clicks on the progress bar
  const handleProgressClick = useCallback(
    async (e: React.MouseEvent<HTMLDivElement>) => {
      if (!youtubePlayer) return;

      // Stop event propagation to prevent parent swipe handling
      e.stopPropagation();
      
      const progressBar = e.currentTarget;
      const rect = progressBar.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const percentage = (x / rect.width) * 100;

      const targetTime =
        startTimeInSeconds + (percentage / 100) * segmentDuration;
      const clampedTime = Math.max(
        startTimeInSeconds,
        Math.min(endTimeInSeconds, targetTime)
      );

      try {
        await youtubePlayer.seekTo(clampedTime, true);
        setCurrentTime(clampedTime);
        setProgress(
          ((clampedTime - startTimeInSeconds) / segmentDuration) * 100
        );
        if (onTimeUpdate) {
          onTimeUpdate(clampedTime);
        }
      } catch (error) {
        console.error("Error seeking to time:", error);
      }
    },
    [
      youtubePlayer,
      startTimeInSeconds,
      endTimeInSeconds,
      segmentDuration,
      onTimeUpdate,
    ]
  );



  // Track the last time we sent a seek command to the YouTube API
  const lastSeekTimeRef = useRef<number>(0);
  // Track the pending seek position to apply on mouseup
  const pendingSeekTimeRef = useRef<number | null>(null);
  // Track whether we're in a RAF (requestAnimationFrame) cycle
  const isInAnimationFrameRef = useRef<boolean>(false);

  // Perform a throttled seek operation for desktop to improve performance
  const throttledSeek = useCallback((clientX: number) => {
    const progressBar = progressBarRef.current;
    if (!progressBar || !youtubePlayer) return;
    
    const rect = progressBar.getBoundingClientRect();
    const x = Math.max(0, Math.min(rect.width, clientX - rect.left));
    const percentage = (x / rect.width) * 100;
    
    // Always update visual progress immediately using RAF
    if (!isInAnimationFrameRef.current) {
      isInAnimationFrameRef.current = true;
      requestAnimationFrame(() => {
        // Update progress bar visually
        setProgress(percentage);
        isInAnimationFrameRef.current = false;
      });
    }
    
    // Calculate time based on percentage
    const targetTime = startTimeInSeconds + (percentage / 100) * segmentDuration;
    const clampedTime = Math.max(
      startTimeInSeconds,
      Math.min(endTimeInSeconds, targetTime)
    );
    
    // Update time display
    setCurrentTime(clampedTime);
    
    // Store the pending seek position
    pendingSeekTimeRef.current = clampedTime;
    
    // Only call YouTube API if enough time has passed (throttle to 5 seeks per second max)
    const now = Date.now();
    if (now - lastSeekTimeRef.current > 200) {  // 200ms = 5 seeks per second 
      lastSeekTimeRef.current = now;
      youtubePlayer.seekTo(clampedTime, true);
      if (onTimeUpdate) {
        onTimeUpdate(clampedTime);
      }
    }
  }, [youtubePlayer, startTimeInSeconds, endTimeInSeconds, segmentDuration, onTimeUpdate]);

  // Handle mouse move during drag
  const handleMouseMove = useCallback((e: MouseEvent) => {
    // Extra safety check - only process if mouse button is actually pressed
    // This provides a fallback in case we missed the mouseup event
    if (e.buttons === 0) {
      // Mouse button is no longer pressed, clean up immediately
      handleDragEndDocument(e);
      return;
    }
    
    // Call our throttled seek function
    throttledSeek(e.clientX);
  }, [throttledSeek]);

  // Flag to track if we've started a drag operation
  const isDraggingRef = useRef<boolean>(false);

  // Efficient mouse drag implementation
  const handleDragStart = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsDragging(true);
    isDraggingRef.current = true;
    
    // Reset tracking refs
    lastSeekTimeRef.current = 0;
    pendingSeekTimeRef.current = null;
    
    // Add multiple document-level event listeners to ensure we catch all events
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleDragEndDocument);
    document.addEventListener('mouseleave', handleDragEndDocument);
    document.addEventListener('mouseout', checkIfMouseLeftWindow);
    window.addEventListener('blur', handleDragEndDocument);
    
    // Safety timeout - force release after 10 seconds even if we miss events
    const safetyTimeoutId = setTimeout(() => {
      if (isDraggingRef.current) {
        console.log('Safety timeout triggered to release drag');
        handleDragEndDocument(new MouseEvent('mouseup'));
      }
    }, 10000);
    
    // Store the timeout ID for cleanup
    return () => clearTimeout(safetyTimeoutId);
  };

  // Check if mouse has left the window entirely
  const checkIfMouseLeftWindow = useCallback((e: MouseEvent) => {
    // Check if mouse has left the document boundaries
    if (!e.relatedTarget || 
        !(e.relatedTarget instanceof Element) || 
        e.relatedTarget.nodeName === 'HTML') {
      // Mouse has left the window, end drag operation
      handleDragEndDocument(e);
    }
  }, []);

  // Handle mouse up anywhere in the document
  const handleDragEndDocument = useCallback((e: MouseEvent) => {
    // Prevent handling if we're already cleaned up
    if (!isDraggingRef.current) return;
    
    // Mark as not dragging first to prevent re-entry
    isDraggingRef.current = false;
    
    // Clean up ALL event listeners we've added
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleDragEndDocument);
    document.removeEventListener('mouseleave', handleDragEndDocument);
    document.removeEventListener('mouseout', checkIfMouseLeftWindow);
    window.removeEventListener('blur', handleDragEndDocument);
    
    // Apply the final seek if there's a pending position
    if (pendingSeekTimeRef.current !== null && youtubePlayer) {
      youtubePlayer.seekTo(pendingSeekTimeRef.current, true);
      if (onTimeUpdate) {
        onTimeUpdate(pendingSeekTimeRef.current);
      }
      pendingSeekTimeRef.current = null;
    }
    
    // Update state AFTER all other operations
    setIsDragging(false);
  }, [youtubePlayer, handleMouseMove, checkIfMouseLeftWindow, onTimeUpdate]);

  // Standard mouse up handler for when released over the bar
  const handleDragEnd = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Actual cleanup is handled by document handler
  };
  
  // Define a ref for the progress bar container
  const progressBarRef = useRef<HTMLDivElement>(null);
  
  // Touch devices need more immediate feedback, so we use a direct seek operation
  const performSeek = (clientX: number) => {
    const progressBar = progressBarRef.current;
    if (!progressBar || !youtubePlayer) return;
    
    const rect = progressBar.getBoundingClientRect();
    const x = Math.max(0, Math.min(rect.width, clientX - rect.left));
    const percentage = (x / rect.width) * 100;
    
    // Use requestAnimationFrame for smoother visual updates
    requestAnimationFrame(() => {
      // Update progress bar visually
      setProgress(percentage);
    });
    
    // Calculate time based on percentage
    const targetTime = startTimeInSeconds + (percentage / 100) * segmentDuration;
    const clampedTime = Math.max(
      startTimeInSeconds,
      Math.min(endTimeInSeconds, targetTime)
    );
    
    // Update time display and video position
    setCurrentTime(clampedTime);
    youtubePlayer.seekTo(clampedTime, true);
    if (onTimeUpdate) {
      onTimeUpdate(clampedTime);
    }
  };
  
  // Handle touch events directly on the DOM level to prevent swipe interference
  // Track touch interaction state
  const isTouchDraggingRef = useRef<boolean>(false);
  
  useEffect(() => {
    const progressBar = progressBarRef.current;
    if (!progressBar) return;
    
    const handleTouchStart = (e: TouchEvent) => {
      // Only stopPropagation is needed here - to prevent YtSharp from detecting this touch
      e.stopPropagation();
      setIsDragging(true);
      isTouchDraggingRef.current = true;
      
      // Add global handlers to capture all further touch events
      document.addEventListener('touchmove', handleTouchMove, { passive: false, capture: true });
      document.addEventListener('touchend', handleTouchEnd, { capture: true });
      document.addEventListener('touchcancel', handleTouchEnd, { capture: true });
      window.addEventListener('blur', handleTouchEnd);
      
      // Safety timeout - force release after 10 seconds even if we miss events
      const safetyTimeoutId = setTimeout(() => {
        if (isTouchDraggingRef.current) {
          console.log('Safety timeout triggered for touch drag');
          handleTouchEnd(new TouchEvent('touchend'));
        }
      }, 10000);
      
      return () => clearTimeout(safetyTimeoutId);
    };
    
    const handleTouchMove = (e: TouchEvent) => {
      // Exit if we're not dragging (safety)
      if (!isTouchDraggingRef.current) return;
      
      // preventDefault is critical here to prevent page scrolling
      // stopPropagation prevents YtSharp from receiving the event
      e.preventDefault();
      e.stopPropagation();
      
      // Perform seek operation with current touch position
      if (e.touches[0]) {
        performSeek(e.touches[0].clientX);
      }
    };
    
    const handleTouchEnd = (e: TouchEvent) => {
      // Skip if we're already cleaned up
      if (!isTouchDraggingRef.current) return;
      
      // Mark as not dragging first to prevent re-entry
      isTouchDraggingRef.current = false;
      
      // Remove ALL global handlers
      document.removeEventListener('touchmove', handleTouchMove, { capture: true });
      document.removeEventListener('touchend', handleTouchEnd, { capture: true });
      document.removeEventListener('touchcancel', handleTouchEnd, { capture: true });
      window.removeEventListener('blur', handleTouchEnd);
      
      // Update state after cleanup
      setIsDragging(false);
    };
    
    // Add touch start handler directly to the progress bar
    progressBar.addEventListener('touchstart', handleTouchStart, { passive: false });
    
    return () => {
      progressBar.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove, { capture: true });
      document.removeEventListener('touchend', handleTouchEnd, { capture: true });
    };
  }, [youtubePlayer, startTimeInSeconds, endTimeInSeconds, segmentDuration, onTimeUpdate]);

  // This is only needed for touch events to prevent YtSharp from detecting them
  // through React's synthetic events (our direct DOM handlers handle the actual logic)
  const blockEvent = (e: React.TouchEvent) => {
    // Just need to stop propagation to prevent YtSharp from detecting the touch
    e.stopPropagation();
  };

  const tabId = useIdeasNodeStore((state) => state.tabId);

  const webExtensionId = import.meta.env.VITE_CHROME_EXTENSION_ID;

  const hoverAtTime = (timeInSeconds) => {
    sendExtensionMessage(webExtensionId, {
      message: "seek_yt_video_to_time",
      tabId: tabId,
      targetTimeInSecs: timeInSeconds,
    });
  };

  const mouseOutFromTime = (timeInSeconds) => {
    sendExtensionMessage(webExtensionId, {
      message: "mouse_out_yt_video_from_time",
      tabId: tabId,
      targetTimeInSecs: timeInSeconds,
    });
  };

  const playAtTime = (timeInSeconds) => {
    sendExtensionMessage(webExtensionId, {
      message: "play_yt_video_to_time",
      tabId: tabId,
      targetTimeInSecs: timeInSeconds,
    });
  };

  return (
    <div 
      className={`ytSegmentProgressBar ${containerStyles}`}
      onClick={(e) => e.stopPropagation()}
      onTouchStart={(e) => {
        e.stopPropagation();
        e.preventDefault();
      }}
      onTouchMove={(e) => {
        e.stopPropagation();
        e.preventDefault();
      }}
      onTouchEnd={(e) => {
        e.stopPropagation();
        e.preventDefault();
      }}
      data-no-swipe="true"
    >
      <div 
        className={`current-time progressTime ${currentTimeStyles}`}
        onClick={(e) => e.stopPropagation()}
        data-no-swipe="true"
      >
        {formatTime(currentTime)}
      </div>
      <div
        ref={progressBarRef}
        className={`progressBarContainer ${getProgressBarContainerStyles(isHovered)}`}
        // Support for clicks (desktop) and light touch events (backup)
        onClick={(e) => {
          // Stop propagation is needed to prevent slide navigation
          e.stopPropagation();
          // For desktop users, allow click-to-seek
          handleProgressClick(e);
        }}
        // Desktop mouse events - only need stopPropagation
        // to prevent YtSharp from handling these events
        onMouseDown={(e) => {
          e.stopPropagation();
          handleDragStart(e);
        }}
        onMouseUp={(e) => {
          e.stopPropagation();
          handleDragEnd(e);
        }}
        onMouseLeave={(e) => {
          e.stopPropagation();
          handleDragEnd(e);
        }}
        // Block React's synthetic events for touch
        // The real handlers are attached directly to the DOM
        onTouchStart={blockEvent}
        onTouchMove={blockEvent}
        onTouchEnd={blockEvent}
        data-no-swipe="true"
      >
        <div className={`progressBar ${getProgressStyles(progress)}`} />
        <div className={`progressHandle ${getHandleStyles(progress, isHovered)}`} />
      </div>
      <div className={`progressTimeDisplay ${getTimeDisplayStyles(isHovered)}`}>
        <span
          className="progressTimeMarker"
          onMouseOver={() => hoverAtTime(startTimeInSeconds)}
          onMouseLeave={() => mouseOutFromTime(startTimeInSeconds)}
          onClick={() => playAtTime(startTimeInSeconds)}
        >
          {formatTime(startTimeInSeconds)}
        </span>
        <span
          className="progressTimeMarker"
          onMouseOver={() => hoverAtTime(endTimeInSeconds)}
          onMouseLeave={() => mouseOutFromTime(endTimeInSeconds)}
          onClick={() => playAtTime(endTimeInSeconds)}
        >
          {formatTime(endTimeInSeconds)}
        </span>
      </div>
    </div>
  );
};

export default SegmentProgressBar;
